/**
 * GuidePage – Quick reference guide to the whole app.
 *
 * One scrollable page that answers "how does LingoFlow actually work?" with
 * minimalist inline SVG infographics. Public (no auth required) so we can
 * link to it from the landing page. Content is fully static — no backend
 * dependency.
 *
 * Sections (in order):
 *   1. The ideal journey flow diagram (3 phases → 5 steps)
 *   2. Modules — each with its own icon + one-paragraph explainer
 *      a. Newcomer Flashcards
 *      b. Sound Foundations
 *      c. Prep Screen
 *      d. Conversations (Free Talk / Scenarios / Teacher Mode)
 *      e. Post-Session Review
 *      f. Adaptive Skill Engine
 *   3. Best-practice tips
 *   4. FAQ
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CaretLeft,
  Sparkle,
  Cards,
  Waveform,
  Target,
  ChatCircleDots,
  ClipboardText,
  ChartLineUp,
  Headphones,
  Clock,
  Smiley,
  Microphone,
  ArrowRight,
  CaretDown,
  Compass,
  Star,
} from '@phosphor-icons/react';

export default function GuidePage() {
  return (
    <div className="lf-shell flex flex-col px-6 py-8" data-testid="guide-page">
      <div className="flex items-center gap-2">
        <Link
          to={-1}
          className="grid h-10 w-10 place-items-center rounded-full border border-[var(--lf-border)] bg-[var(--lf-surface)]"
          data-testid="guide-back"
        >
          <CaretLeft size={18} />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
            Quick reference
          </div>
          <h1 className="text-2xl font-bold">How LingoFlow works</h1>
        </div>
      </div>

      <motion.p
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-5 text-[var(--lf-text)]"
      >
        A speaking-first path from day one. You'll warm up, nail the key sounds,
        then talk — with an AI partner that listens, adapts, and cheers you on.
      </motion.p>

      {/* 1. Ideal journey */}
      <Section
        icon={<Compass size={18} weight="duotone" />}
        title="Your 3-phase journey"
        testid="guide-journey"
      >
        <JourneyFlow />
      </Section>

      {/* 2. Modules */}
      <Section
        icon={<Sparkle size={18} weight="duotone" />}
        title="The modules, at a glance"
        testid="guide-modules"
      >
        <div className="space-y-3">
          <ModuleCard
            icon={<Cards size={22} weight="duotone" />}
            tag="Pre-A1 · Phase 1"
            title="Newcomer Flashcards"
            copy="50 starter cards: greetings, numbers 0-10, days of the week, 12 concrete words with pictures, and 10 survival phrases. Tap Listen, press-and-record, and rate yourself Again / Good / Easy."
            viz={<FlashcardsInfographic />}
          />
          <ModuleCard
            icon={<Waveform size={22} weight="duotone" />}
            tag="Pre-A1 · Phase 1"
            title="Sound Foundations"
            copy="Five short lessons that teach the sounds most beginners trip on: the 5 pure vowels, Ñ, R vs RR, the Spanish J, and the silent H. Each lesson: watch → listen → pick a minimal pair → record."
            viz={<SoundsInfographic />}
          />
          <ModuleCard
            icon={<Target size={22} weight="duotone" />}
            tag="Every session · Phase 2"
            title="Prep Screen"
            copy="Before each conversation we show you 3-6 warm-up words tailored to your level + the scenario. Tap to focus — the AI will gently encourage you to use them during the chat."
            viz={<PrepInfographic />}
          />
          <ModuleCard
            icon={<ChatCircleDots size={22} weight="duotone" />}
            tag="A1+ · Phase 2"
            title="Conversations"
            copy="Three ways to talk: Free Talk for open chat, Scenarios (café, interview, dinner with a friend…) for roleplay, and Teacher Mode for a guided lesson with a mini-lesson plan and quizzes."
            viz={<ConversationsInfographic />}
          />
          <ModuleCard
            icon={<ClipboardText size={22} weight="duotone" />}
            tag="After every chat · Phase 3"
            title="Post-Session Review"
            copy="We replay the session and show you what went well, any words you mispronounced (with a Pronounce-it-now drill), and your suggested next focus words — all in one screen."
            viz={<ReviewInfographic />}
          />
          <ModuleCard
            icon={<ChartLineUp size={22} weight="duotone" />}
            tag="Always running · Phase 3"
            title="Adaptive Skill Engine"
            copy="Six dimensions — listening, speaking, vocabulary, pronunciation, confidence, repair — update after every session. The AI uses them to pick your pacing, complexity, and support ratio for the next chat."
            viz={<SkillsInfographic />}
          />
        </div>
      </Section>

      {/* 3. Best-practice tips */}
      <Section
        icon={<Star size={18} weight="duotone" />}
        title="How to get the most out of it"
        testid="guide-tips"
      >
        <div className="space-y-2.5">
          <Tip icon={<Headphones size={16} weight="fill" />}>
            Use headphones. The AI speaks fast when you're ready — headphones make hearing the vowels much easier.
          </Tip>
          <Tip icon={<Clock size={16} weight="fill" />}>
            Short and often beats long and rare. Aim for **5-10 minutes a day** over big weekend sessions.
          </Tip>
          <Tip icon={<Microphone size={16} weight="fill" />}>
            Don't chase perfect — chase **being understood**. Our scoring is forgiving; just keep talking.
          </Tip>
          <Tip icon={<Target size={16} weight="fill" />}>
            Actually tap the focus words on the Prep Screen. The AI is listening for them and will nudge.
          </Tip>
          <Tip icon={<Smiley size={16} weight="fill" />}>
            It's okay to request **"más despacio"** or **"¿puede repetir?"** — those signals help the engine adapt faster.
          </Tip>
        </div>
      </Section>

      {/* 4. FAQ */}
      <Section
        icon={<CaretDown size={18} weight="duotone" />}
        title="Quick FAQ"
        testid="guide-faq"
      >
        <div className="space-y-2">
          <FAQ
            q="I've never spoken Spanish before. Where do I start?"
            a="During signup, pick “No, this is my first time.” We'll put you in Newcomer Mode, starting with flashcards then Sound Foundations, so you're never dropped into a conversation cold."
          />
          <FAQ
            q="I already know some Spanish. Do I have to do flashcards?"
            a="No — pick “Yes, I know a little” at signup and you'll skip straight to Free Talk / Scenarios. Flashcards are always available under /newcomer if you want to warm up."
          />
          <FAQ
            q="Do I have to talk, or can I type?"
            a="Talking builds the skill you actually want, so it's the default. You can still use the Help / Translate / Repeat buttons whenever you're stuck."
          />
          <FAQ
            q="How does the AI know how fast to talk?"
            a="Your Adaptive Skill Engine updates every session. Listening + confidence scores drive TTS speed and sentence length; your chosen comfort_level is the floor."
          />
          <FAQ
            q="What if I hate a scenario?"
            a="Hit End Session anytime — no penalty. Your review and skill snapshot still save."
          />
          <FAQ
            q="Does LingoFlow work offline?"
            a="No. Speech recognition, TTS, and the tutor all need the cloud. We cache your session history locally so the app opens fast, but practice requires a connection."
          />
        </div>
      </Section>

      <div className="mt-10 flex items-center justify-center gap-3 text-sm text-[var(--lf-text-muted)]">
        <Link
          to="/home"
          className="lf-btn lf-btn-primary"
          data-testid="guide-home-cta"
        >
          Back to Home <ArrowRight size={16} weight="bold" />
        </Link>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Section wrapper
// --------------------------------------------------------------------------- //
function Section({ icon, title, children, testid }) {
  return (
    <section className="mt-8" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--lf-text-muted)]">
        <span className="text-[var(--lf-primary)]">{icon}</span>
        {title}
      </div>
      {children}
    </section>
  );
}

function ModuleCard({ icon, tag, title, copy, viz }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      className="lf-card p-5"
    >
      <div className="flex items-start gap-4">
        <div className="grid h-12 w-12 flex-shrink-0 place-items-center rounded-2xl bg-[var(--lf-surface-muted)] text-[var(--lf-primary)]">
          {icon}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[var(--lf-primary)] font-semibold">
            {tag}
          </div>
          <div className="mt-0.5 text-lg font-bold leading-tight">{title}</div>
          <p className="mt-1 text-sm text-[var(--lf-text-muted)] leading-relaxed">
            {copy}
          </p>
        </div>
      </div>
      {viz ? <div className="mt-4 border-t border-[var(--lf-border)] pt-4">{viz}</div> : null}
    </motion.div>
  );
}

function Tip({ icon, children }) {
  return (
    <div className="lf-card flex items-start gap-3 p-3.5 text-sm">
      <span className="mt-0.5 text-[var(--lf-primary)]">{icon}</span>
      <span>{formatBold(children)}</span>
    </div>
  );
}

function FAQ({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className={`lf-card overflow-hidden transition-all ${open ? 'bg-[var(--lf-surface)]' : ''}`}
      data-testid={`guide-faq-${q.slice(0, 14).replace(/\W+/g, '-').toLowerCase()}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 p-3.5 text-left"
      >
        <span className="font-semibold text-sm">{q}</span>
        <CaretDown
          size={16}
          className={`flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open ? (
        <div className="border-t border-[var(--lf-border)] px-3.5 py-3 text-sm text-[var(--lf-text-muted)]">
          {a}
        </div>
      ) : null}
    </div>
  );
}

// Convert **bold** inside Tip text.
function formatBold(children) {
  if (typeof children !== 'string') return children;
  const parts = children.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) =>
    p.startsWith('**') && p.endsWith('**') ? (
      <strong key={i}>{p.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{p}</span>
    ),
  );
}

// --------------------------------------------------------------------------- //
// Infographics (inline SVG-style cards)
// --------------------------------------------------------------------------- //
function JourneyFlow() {
  const phases = [
    { kind: 'step', name: 'Sign up', sub: '10 seconds — tell us what you know.', color: 'var(--lf-accent)' },
    { kind: 'phase', name: 'Phase 1 · Warm up' },
    { kind: 'step', name: 'Flashcards', sub: '50 starter words with audio + pronunciation scoring.' },
    { kind: 'step', name: 'Sound Foundations', sub: '5 short lessons — master the tricky sounds.' },
    { kind: 'phase', name: 'Phase 2 · Practice' },
    { kind: 'step', name: 'Prep Screen', sub: '3-6 focus words before every chat.' },
    { kind: 'step', name: 'Conversation', sub: 'Free Talk, Scenarios, or Teacher Mode.' },
    { kind: 'phase', name: 'Phase 3 · Grow' },
    { kind: 'step', name: 'Review + Skill snapshot', sub: 'Feedback in plain language. Repeat daily.' },
  ];
  return (
    <div className="lf-card p-5">
      <div className="space-y-2" data-testid="guide-journey-flow">
        {phases.map((p, i) => (
          p.kind === 'phase' ? (
            <div
              key={`p-${i}`}
              className="mt-3 text-[10px] uppercase tracking-widest font-semibold text-[var(--lf-primary)]"
            >
              {p.name}
            </div>
          ) : (
            <div
              key={`s-${i}`}
              className="flex items-start gap-3 rounded-xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-3"
            >
              <div
                className="grid h-7 w-7 flex-shrink-0 place-items-center rounded-full bg-[var(--lf-primary)] text-white text-xs font-bold"
                aria-hidden
              >
                {phases.filter((x, j) => x.kind === 'step' && j <= i).length}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">{p.name}</div>
                <div className="text-xs text-[var(--lf-text-muted)]">{p.sub}</div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

function FlashcardsInfographic() {
  const chips = [
    { label: 'Greetings', count: 10, hue: '#F9B87D' },
    { label: 'Numbers', count: 11, hue: '#F6C97D' },
    { label: 'Days', count: 7, hue: '#F0DA8A' },
    { label: 'First words', count: 12, hue: '#B9D4A6' },
    { label: 'Survival', count: 10, hue: '#A8C5D9' },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c) => (
        <div
          key={c.label}
          className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
          style={{ backgroundColor: c.hue + '33', color: '#2b2620' }}
        >
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: c.hue }} />
          {c.label} · {c.count}
        </div>
      ))}
      <div className="w-full text-xs text-[var(--lf-text-muted)]">= 50 cards total</div>
    </div>
  );
}

function SoundsInfographic() {
  const lessons = [
    { sym: 'a e i o u', name: 'Pure vowels' },
    { sym: 'ñ', name: 'Enye' },
    { sym: 'r / rr', name: 'Tap vs trill' },
    { sym: 'j', name: 'Jota' },
    { sym: 'h', name: 'Silent' },
  ];
  return (
    <div className="grid grid-cols-5 gap-2">
      {lessons.map((l) => (
        <div
          key={l.name}
          className="grid place-items-center rounded-xl border border-[var(--lf-border)] bg-[var(--lf-surface)] py-2 px-1 text-center"
        >
          <div className="font-serif text-lg font-bold text-[var(--lf-primary)] leading-none">
            {l.sym}
          </div>
          <div className="mt-1 text-[10px] text-[var(--lf-text-muted)] leading-tight">
            {l.name}
          </div>
        </div>
      ))}
    </div>
  );
}

function PrepInfographic() {
  const words = ['hola', 'me gustaría', 'por favor', 'gracias', '¿cuánto?', 'para llevar'];
  return (
    <div className="flex flex-wrap gap-1.5">
      {words.map((w, i) => (
        <span
          key={w}
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
            i < 3
              ? 'bg-[var(--lf-primary)] text-white'
              : 'bg-[var(--lf-surface-muted)] text-[var(--lf-text)] border border-[var(--lf-border)]'
          }`}
        >
          {i < 3 ? '★ ' : ''}
          {w}
        </span>
      ))}
    </div>
  );
}

function ConversationsInfographic() {
  const modes = [
    { name: 'Free Talk', sub: 'Open chat — your pace.' },
    { name: 'Scenarios', sub: 'Roleplay a real situation.' },
    { name: 'Teacher Mode', sub: 'Guided lesson + quiz.' },
  ];
  return (
    <div className="grid grid-cols-3 gap-2">
      {modes.map((m) => (
        <div
          key={m.name}
          className="rounded-xl border border-[var(--lf-border)] bg-[var(--lf-surface)] p-2.5"
        >
          <div className="text-xs font-bold">{m.name}</div>
          <div className="text-[10px] text-[var(--lf-text-muted)] leading-tight">
            {m.sub}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewInfographic() {
  return (
    <div className="grid grid-cols-2 gap-2 text-xs">
      <div className="rounded-xl bg-[#E9F3E4] p-2.5">
        <div className="font-bold text-[#2f7a22]">What went well</div>
        <div className="text-[#2f7a22]/80">Used 'me gustaría' naturally.</div>
      </div>
      <div className="rounded-xl bg-[#FFE7DB] p-2.5">
        <div className="font-bold text-[#b34d1e]">Work on</div>
        <div className="text-[#b34d1e]/80">Gender agreement on adjectives.</div>
      </div>
      <div className="col-span-2 rounded-xl bg-[var(--lf-surface-muted)] p-2.5">
        <div className="font-bold">Suggested next focus</div>
        <div className="text-[var(--lf-text-muted)]">porque · aunque · sin embargo</div>
      </div>
    </div>
  );
}

function SkillsInfographic() {
  const dims = [
    { name: 'Listening', v: 0.66 },
    { name: 'Speaking', v: 0.54 },
    { name: 'Vocabulary', v: 0.61 },
    { name: 'Pronunciation', v: 0.48 },
    { name: 'Confidence', v: 0.7 },
    { name: 'Repair', v: 0.55 },
  ];
  return (
    <div className="space-y-1.5">
      {dims.map((d) => (
        <div key={d.name} className="flex items-center gap-3 text-xs">
          <div className="w-24 text-[var(--lf-text-muted)]">{d.name}</div>
          <div className="flex-1 h-1.5 overflow-hidden rounded-full bg-[var(--lf-surface-muted)]">
            <div
              className="h-full bg-[var(--lf-primary)]"
              style={{ width: `${Math.round(d.v * 100)}%` }}
            />
          </div>
          <div className="w-8 text-right font-mono text-[var(--lf-text-muted)]">
            {d.v.toFixed(2)}
          </div>
        </div>
      ))}
    </div>
  );
}
